"use client";

import React, { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Input } from "../../ui/input";
import { Loader2, Search, Upload, ArrowLeft, Play, ChevronDown } from "lucide-react";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import RFORevenueAnalyticalDashboard from "./RFORevenueAnalyticalDashboard";

interface RFOShipAndBillIPETestingProps {
  onBack?: () => void;
}

interface ExcelColumn {
  name: string;
  value: string;
}

const REQUIRED_FIELD_KEYS: Array<{ key: string; label: string }> = [
  { key: "invoice_date", label: "Invoice Date" },
  { key: "customer_name", label: "Customer Name" },
];

const OPTIONAL_FIELD_KEYS: Array<{ key: string; label: string }> = [
  { key: "customer_country", label: "Customer Country" },
  { key: "plant_location", label: "Plant Location" },
  { key: "invoice_amount", label: "Invoice Amount" },
  { key: "export_domestic", label: "Export/Domestic" },
  { key: "incoterm", label: "Incoterm" },
  { key: "transaction_type", label: "Transaction Type" },
  { key: "product_name", label: "Product Name" },
  { key: "billing_quantity", label: "Billing Quantity" },
];

const FIELD_KEYS = [...REQUIRED_FIELD_KEYS, ...OPTIONAL_FIELD_KEYS];

const FILE_SECTION = "Execution_RFO_SSR";

export default function RFOShipAndBillIPETesting({ onBack }: RFOShipAndBillIPETestingProps) {
  const { isArchivalMode } = useArchivalMode();
  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingExcelColumns, setIsLoadingExcelColumns] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [ipeTestingOpen, setIpeTestingOpen] = useState(false);

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles(FILE_SECTION);
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  useEffect(() => {
    searchForFiles();
  }, []);

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv";
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(",")[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error("Failed to read file"));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              const fyYear = getFY_YEAR();
              if (!fyYear) {
                throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
              }

              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: fyYear,
                sectionList: [FILE_SECTION],
              });

              if (result.success) {
                setSelectedFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadExcelColumns = async () => {
    if (!selectedFile) return;
    setIsLoadingExcelColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(selectedFile);
        if (result.ok && result.columns) {
          const columns = result.columns.map((col: string) => ({ name: col, value: col }));
          setExcelColumns(columns);
        }
      }
    } catch {
      // Keep silent to preserve the same UX pattern as payroll pages.
    } finally {
      setIsLoadingExcelColumns(false);
    }
  };

  const handleMappingChange = (fieldKey: string, columnName: string) => {
    setColumnMappings((prev) => ({ ...prev, [fieldKey]: columnName }));
  };

  const requiredFieldsMapped = REQUIRED_FIELD_KEYS.every(
    (f) => columnMappings[f.key] && columnMappings[f.key].trim().length > 0,
  );

  const executeIPE = async () => {
    if (!selectedFile || !requiredFieldsMapped) return;
    setProcessingStatus("running");

    try {
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const column_mapping: Record<string, string> = {};
      for (const f of FIELD_KEYS) {
        const v = columnMappings[f.key];
        column_mapping[f.key] = v && v.trim() ? v.trim() : "";
      }

      const result = await window.payroll.run("execute_rfo_ship_bill_ipe_dashboard", {
        inputFiles: [],
        options: {
          column_mapping,
          input_file_name: selectedFile,
          reference_value: selectedFile,
          engagement_file: "EngagementDetails.json",
          output_file_name: "Execution_RFO_Dashboard.json",
          input_folder: "client",
          output_folder: "juggernaut",
          db_section: ["Execution_RFO_Dashboard"],
        },
      });

      if (result.ok) {
        setProcessingStatus("completed");
      } else {
        setProcessingStatus("error");
      }
    } catch {
      setProcessingStatus("error");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Ship and Bill - IPE Testing</h2>
          <p className="text-gray-400">
            Map Excel columns, then run to build Revenue Analytical Dashboard JSON (SharePoint)
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader className={`space-y-0 ${ipeTestingOpen ? "pb-0" : "pb-5"}`}>
          <button
            type="button"
            onClick={() => setIpeTestingOpen((o) => !o)}
            className="flex w-full items-center justify-between gap-3 rounded-lg text-left outline-none ring-offset-background transition hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-cyan-500/50"
            aria-expanded={ipeTestingOpen}
          >
            <CardTitle className="text-lg font-semibold text-white">IPE Testing</CardTitle>
            <ChevronDown
              className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 ${ipeTestingOpen ? "rotate-180" : ""}`}
            />
          </button>
        </CardHeader>
        {ipeTestingOpen && (
          <CardContent className="space-y-6 pt-6">
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <Search className="h-5 w-5 text-blue-500" />
            Step 1: Search for Existing Files
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "searching" && (
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching for files...</span>
              </div>
            )}
            {fileSearchStatus === "found" && <span className="text-green-400 font-bold"> FOUND!</span>}
            {fileSearchStatus === "not_found" && <span className="text-yellow-400">No files found</span>}
            {fileSearchStatus === "idle" && <span className="text-gray-400">Searching for files...</span>}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select File</Label>
                <Select value={selectedFile} onValueChange={setSelectedFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedFile && (
                <div className="flex items-center gap-3">
                  <Button
                    onClick={loadExcelColumns}
                    disabled={!selectedFile || isLoadingExcelColumns}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isLoadingExcelColumns ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...
                      </>
                    ) : (
                      "Load Columns"
                    )}
                  </Button>
                  {excelColumns.length > 0 && (
                    <span className="text-green-400 text-sm">{excelColumns.length} columns loaded</span>
                  )}
                </div>
              )}
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button
                  onClick={uploadFile}
                  disabled={uploadStatus === "uploading"}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {uploadStatus === "uploading" ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" /> Upload File
                    </>
                  )}
                </Button>
                {uploadStatus === "completed" && <span className="text-green-400">File uploaded successfully!</span>}
                {uploadStatus === "error" && <span className="text-red-400">Upload failed</span>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Step 2: Map Columns</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-400">
            <span className="text-rose-300/90">*</span> Required for Run. Other fields are optional; charts appear only when the JSON
            includes analytics for mapped columns.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {REQUIRED_FIELD_KEYS.map(({ key: fieldKey, label: fieldLabel }) => (
              <div key={fieldKey} className="space-y-2">
                <Label className="text-white">
                  {fieldLabel} <span className="text-rose-300/90">*</span>
                </Label>
                <Select
                  value={columnMappings[fieldKey] || ""}
                  onValueChange={(v) => handleMappingChange(fieldKey, v)}
                  disabled={excelColumns.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={excelColumns.length === 0 ? "Load columns first" : "Select column"} />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {excelColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
            {OPTIONAL_FIELD_KEYS.map(({ key: fieldKey, label: fieldLabel }) => (
              <div key={fieldKey} className="space-y-2">
                <Label className="text-white">{fieldLabel}</Label>
                <Select
                  value={columnMappings[fieldKey] || ""}
                  onValueChange={(v) => handleMappingChange(fieldKey, v)}
                  disabled={excelColumns.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={excelColumns.length === 0 ? "Load columns first" : "Optional"} />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {excelColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <Play className="h-5 w-5 text-green-500" />
            Run Revenue Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={executeIPE}
              disabled={isArchivalMode || !selectedFile || !requiredFieldsMapped || processingStatus === "running"}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {processingStatus === "running" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...
                </>
              ) : (
                "Run"
              )}
            </Button>
            {processingStatus === "completed" && <span className="text-green-400">Successful</span>}
            {processingStatus === "error" && <span className="text-red-400">Failed</span>}
          </div>
        </CardContent>
      </Card>
          </CardContent>
        )}
      </Card>

      <RFORevenueAnalyticalDashboard />
    </div>
  );
}
